import type { ImageField } from "@prismicio/client"
import classNames from "classnames"

import DEFAULT_STOREFRONT from "@bounce/assets/images/default-storefront-600x600.jpeg"
import { useTranslation } from "@bounce/i18n"
import { theme } from "@bounce/theme"
import type { Nullable } from "@bounce/util"
import { PNImage } from "@bounce/web-components"

import bagsImg from "@bounce/assets/images/two-bags.png"

export type HeroImageProps = {
  href?: Nullable<ImageField>
}

type TrapezoidProps = {
  children?: React.ReactNode
  className?: string
}

const Trapezoid = ({ children, className = "" }: TrapezoidProps) => {
  return (
    <div className={classNames("relative", className)}>
      <div className="absolute inset-0 origin-top transform-gpu [transform:perspective(140px)_rotateX(10deg)_rotateY(-10deg)] lg:origin-bottom lg:[transform:perspective(250px)_rotateX(9deg)_rotateY(-8deg)]">
        {children}
      </div>
    </div>
  )
}

export const HeroImage = ({ href }: HeroImageProps) => {
  const { t } = useTranslation()

  const imageHref = href?.url ? href : DEFAULT_STOREFRONT

  return (
    <div className="relative my-4 flex items-center lg:my-0">
      <div className="relative z-0 order-1 h-[119px] w-[140px] lg:order-2 lg:h-[290px] lg:w-[337px]">
        <PNImage
          priority
          loading="eager"
          data-chromatic="ignore"
          src={bagsImg}
          alt={t("cmp.heroImage.altText", "Two bags to store at a Bounce location")}
          height={290}
          width={337}
          sizes={`(max-width: ${theme.spacing("screen-lg")}px) 140px, 337px`}
        />
      </div>

      <div className="relative z-1 order-2 -ml-10 size-32 pt-4 lg:order-1 lg:-mr-20 lg:ml-0 lg:size-80 lg:pt-11">
        <Trapezoid className="size-[120px] lg:h-[100px] lg:w-[300px]">
          <PNImage
            src={imageHref}
            height={266}
            alt={href?.alt}
            width={320}
            className="h-[100px] w-[144px] rounded-xl border-[3px] border-white object-cover md:border-[7px] lg:h-[266px] lg:w-[320px] lg:rounded-3xl"
            priority
            loading="eager"
            data-chromatic="ignore"
            sizes={`(max-width: ${theme.spacing("screen-lg")}px) 144px, 320px`}
          />
        </Trapezoid>
      </div>
    </div>
  )
}
